import { useEffect } from "react";
import { useOutletContext } from "react-router";
import { Button } from "@/components/Button";
import { KebabMenu } from "@/components/KebabMenu";
import { OnlineStatusBadge } from "@/components/OnlineStatusBadge";
import { Pagination } from "@/components/Pagination";
import { SearchField } from "@/components/SearchField";
import { Select } from "@/components/Select";
import { formatDate } from "@/lib/formatDate";
import { formatRelativeTime } from "@/lib/formatRelativeTime";
import { isOnline } from "@/lib/onlineStatus";
import { PAGE_SIZE } from "@/lib/pagination";
import { plural } from "@/lib/plural";
import type { NetworkingLayoutContext } from "./ConnectorsAndGatewaysLayout";
import type { useEntityList } from "./useEntityList";
import { LoadError } from "@/components/LoadError";
import { EmptyState } from "@/components/EmptyState";

export interface NetworkEntity {
  id: string;
  name: string;
  createdAt: string;
  lastSeenAt: string | null;
}

interface Column<T> {
  header: string;
  render: (entity: T) => React.ReactNode;
}

function formatAbsolute(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function SkeletonRows({
  columns,
  hasActions,
}: {
  columns: number;
  hasActions: boolean;
}) {
  return (
    <>
      {Array.from({ length: 4 }, (_, index) => (
        <tr key={index} className="h-14 animate-pulse">
          <td className="py-2 pr-4">
            <div className="h-3.5 w-36 rounded bg-surface" />
            <div className="mt-1.5 h-2.5 w-48 rounded bg-surface" />
          </td>
          {Array.from({ length: columns }, (_, cell) => (
            <td key={cell} className="py-2 pr-4">
              <div className="h-3 w-20 rounded bg-surface" />
            </td>
          ))}
          {hasActions && <td />}
        </tr>
      ))}
    </>
  );
}

export function EntityListPage<T extends NetworkEntity>({
  list,
  entityLabel,
  entityLabelPlural,
  emptyDescription,
  identityMeta,
  extraColumns = [],
  menuItems,
}: {
  list: ReturnType<typeof useEntityList<T>>;
  entityLabel: string;
  entityLabelPlural: string;
  emptyDescription: string;
  identityMeta?: (entity: T) => string | null;
  extraColumns?: Column<T>[];
  menuItems?: (entity: T) => React.ComponentProps<typeof KebabMenu>["items"];
}) {
  const { setSummary } = useOutletContext<NetworkingLayoutContext>();

  const items = list.items;

  useEffect(() => {
    if (!items) {
      return;
    }

    setSummary({
      total: list.totalCount,
      offline: items.filter((entity) => !isOnline(entity.lastSeenAt)).length,
      entityLabel,
    });

    return () => setSummary(null);
  }, [items, list.totalCount, entityLabel, setSummary]);

  const isLoading = items === null && !list.error;
  const isEmpty = items !== null && list.totalCount === 0;

  return (
    <div>
      <div className="mb-4 flex gap-2">
        <SearchField
          value={list.nameFilter}
          onChange={list.setNameFilter}
          label={`Search ${entityLabelPlural} by name`}
          placeholder={`Search ${entityLabel} name`}
        />
        <Select
          aria-label="Filter by status"
          value={list.statusFilter}
          onChange={(e) => list.setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="Online">Online</option>
          <option value="Offline">Offline</option>
        </Select>
      </div>

      {list.hasFilters && items !== null && (
        <div className="mb-4 flex items-center gap-3 text-sm text-foreground/70">
          <span>Filtered to {plural(list.totalCount, entityLabel)}</span>
          <button
            type="button"
            onClick={list.clearFilters}
            className="rounded text-accent hover:underline focus:outline-none focus:ring-2 focus:ring-accent/20"
          >
            Clear filters
          </button>
        </div>
      )}

      {list.error && !items && (
        <LoadError
          what={entityLabelPlural}
          onRetry={list.retry}
          className="mb-4"
        />
      )}

      {isEmpty ? (
        <EmptyState
          title={
            list.hasFilters
              ? `No ${entityLabelPlural} match these filters`
              : `No ${entityLabelPlural} yet`
          }
          description={
            list.hasFilters
              ? "Try a different name or status."
              : emptyDescription
          }
        >
          {list.hasFilters && (
            <Button onClick={list.clearFilters}>Clear filters</Button>
          )}
        </EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[48rem] text-left text-sm">
            <thead>
              <tr>
                {[
                  entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1),
                  ...extraColumns.map((column) => column.header),
                  "Status",
                  "Heartbeat",
                  "Created",
                ].map((header) => (
                  <th
                    key={header}
                    scope="col"
                    className="border-b border-surface pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wider text-foreground/70"
                  >
                    {header}
                  </th>
                ))}
                {menuItems && (
                  <th scope="col" className="border-b border-surface pb-2.5">
                    <span className="sr-only">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <SkeletonRows
                  columns={extraColumns.length + 3}
                  hasActions={Boolean(menuItems)}
                />
              ) : (
                items?.map((entity) => {
                  const meta = identityMeta?.(entity);

                  return (
                    <tr
                      key={entity.id}
                      className="h-14 border-b border-surface/70 hover:bg-surface"
                    >
                      <td className="py-2 pr-4">
                        <div>{entity.name}</div>
                        {meta && (
                          <div className="truncate font-mono text-xs text-foreground/70">
                            {meta}
                          </div>
                        )}
                      </td>
                      {extraColumns.map((column) => (
                        <td key={column.header} className="py-2 pr-4">
                          {column.render(entity)}
                        </td>
                      ))}
                      <td className="py-2 pr-4">
                        <OnlineStatusBadge lastSeenAt={entity.lastSeenAt} />
                      </td>
                      <td className="py-2 pr-4">
                        {entity.lastSeenAt ? (
                          <>
                            <div className="tabular-nums">
                              {formatRelativeTime(entity.lastSeenAt)}
                            </div>
                            <div className="text-xs tabular-nums text-foreground/70">
                              {formatAbsolute(entity.lastSeenAt)}
                            </div>
                          </>
                        ) : (
                          <span className="text-foreground/70">Never</span>
                        )}
                      </td>
                      <td className="py-2 pr-4 tabular-nums text-foreground/70">
                        {formatDate(entity.createdAt)}
                      </td>
                      {menuItems && (
                        <td className="py-2 text-right">
                          <KebabMenu
                            label={`More actions for ${entity.name}`}
                            items={menuItems(entity)}
                          />
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={list.page}
        totalPages={Math.ceil(list.totalCount / PAGE_SIZE)}
        onPageChange={list.setPage}
      />
    </div>
  );
}
